import { Classes, Icon } from '@blueprintjs/core';
import { useEventPublisher } from 'use-event-emitter-hook';

/**
 * Combines multiple class names into a single string.
 * Filters out undefined or false values.
 *
 * @param {...(string | undefined | false)[]} classNames - Class names to combine.
 * @returns {string} - Combined class names.
 */
export const cx = (...classNames: Array<string | undefined | false>) =>
  classNames.filter(cn => !!cn).join(' ');

/**
 * Renders the appropriate icon for a tree item based on its type and state.
 *
 * @param {object} item - The tree item.
 * @param {boolean} item.isFolder - Whether the item is a folder.
 * @param {object} context - The context of the tree item.
 * @param {boolean} context.isExpanded - Whether the folder is expanded.
 * @returns {JSX.Element} - The rendered icon.
 */
export const renderItemIcon = (item: any, context: any) => {
  const icon = !item.isFolder
    ? 'document'
    : context.isExpanded
    ? 'folder-open'
    : 'folder-close';

  return <Icon icon={icon} size={18} className={Classes.TREE_NODE_ICON} />;
};

/**
 * Handles the context menu click event for a tree item.
 * Publishes an event with the item data and context.
 *
 * @param {React.MouseEvent} e - The click event.
 * @param {object} item - The tree item.
 * @param {boolean} isFolder - Whether the item is a folder.
 */
export const handleContextMenuClick = (
  e: React.MouseEvent,
  item: any,
  isFolder: boolean
) => {
  e.stopPropagation();
  const data = {
    itemData: item.data,
    isEditClicked: true,
    isSection: isFolder,
    eventTarget: e.currentTarget,
  };

  const publisher = useEventPublisher();
  publisher('editClick', data);
};

// Default exports for easier testing
export default {
  cx,
  renderItemIcon,
  handleContextMenuClick,
};
